// @ts-check

/**
 * @file Renders the new bill creation route.
 */

import {Form} from "@kaspernj/api-maker/build/form"
import Text from "@kaspernj/api-maker/build/utils/text"
import FontAwesome6 from "@expo/vector-icons/FontAwesome6"
import _ from "gettext-universal/build/src/translate.js"
import useLocale from "gettext-universal/build/src/use-locale-expo.js"
import {useLocalSearchParams, useRouter} from "expo-router"
import {createRef, memo, useEffect} from "react"
import {shapeComponent, ShapeComponent} from "set-state-compare/build/shape-component.js"
import {Platform} from "react-native"
import {ActivityIndicator, Pressable, View} from "react-native-propforge"
import Button from "../../src/components/button"
import FormTextInput from "../../src/components/form/form-text-input"
import InvoiceGroupsSelect from "../../src/components/invoice-groups/select"
import {Invoice} from "../../src/frontend-models/invoice"
import {InvoiceGroup} from "../../src/frontend-models/invoice-group"
import OrganizationsSelect from "../../src/components/organizations/select"
import {Organization} from "../../src/frontend-models/organization"
import {User} from "../../src/frontend-models/user"
import {userFromProfileCommandResponse} from "../../src/auth/use-current-user"
import buildRoutePath from "../../src/shared/build-route-path"
import errorMessage from "../../src/shared/error-message"
import ScreenTitle from "../../src/components/screen-title"

/** @import {NamedExoticComponent, ReactNode} from "react" */
/** @type {Record<string, object>} */
const styles = {}

/** @typedef {{kind: "error", text: string} | null} BillsStatus */
/** @typedef {{contentBase64: string, contentType: string, filename: string} | null} BillsUploadedFile */

/**
 * Formats a date for the bill date input.
 * @param {Date} date Date to format.
 * @returns {string} ISO date string.
 */
function formatDate(date) {
  const year = String(date.getFullYear())
  const month = String(date.getMonth() + 1).padStart(2, "0")
  const day = String(date.getDate()).padStart(2, "0")
  return `${year}-${month}-${day}`
}

/** @typedef {{[key: string]: unknown}} BillsNewScreenProps */
/** @typedef {{hasOrganizations: boolean, loading: boolean, saving: boolean, selectedInvoiceGroupIds: string[], selectedOrganizationId: string, status: BillsStatus, uploadedFile: BillsUploadedFile, uploadedFileName: string}} BillsNewScreenState */

/**
 * Creates bill records from invoice metadata and optional uploads.
 * @augments {ShapeComponent<BillsNewScreenProps, BillsNewScreenState>}
 */
class BillsNewScreen extends ShapeComponent {
  /** @type {ReturnType<typeof useLocalSearchParams>} */
  params
  /** @type {ReturnType<typeof useRouter>} */
  router
  /** @type {BillsNewScreenState} */
  state = {
    hasOrganizations: false,
    loading: true,
    saving: false,
    selectedInvoiceGroupIds: [],
    selectedOrganizationId: "",
    status: null,
    uploadedFile: null,
    uploadedFileName: ""
  }
  formObjectRef = createRef()
  loadDataRequestId = 0

  /** Initializes translations, route helpers, and form option loading. */
  setup() {
    useLocale()
    const params = useLocalSearchParams()
    const router = useRouter()

    this.params = params
    this.router = router
    useEffect(() => {
      const requestId = ++this.loadDataRequestId
      void this.tt.loadData(requestId)
      return () => {
        if (this.loadDataRequestId === requestId) {
          this.loadDataRequestId += 1
        }
      }
    }, [])
  }

  /**
   * Renders the new bill form.
   * @returns {ReactNode} New bill form content.
   */
  render() {
    const {loading, saving, status, uploadedFileName} = this.s
    return (
      <View
        style={styles.screen ||= {
          flex: 1
        }}
        testID="billsNewScreen"
      >
        <ScreenTitle>{_("New bill")}</ScreenTitle>
        <View
          style={styles.card ||= {
            alignSelf: "center",
            backgroundColor: "#ffffff",
            borderColor: "#d1d5db",
            borderRadius: 8,
            borderWidth: 1,
            maxWidth: 960,
            padding: 24,
            width: "100%"
          }}
          testID="billsNewScreen/card"
        >
          <Text
            style={styles.title ||= {
              color: "#111827",
              fontSize: 28,
              fontWeight: 700
            }}
            testID="billsNewScreen/title"
          >
            {_("New bill")}
          </Text>
          {loading &&
            <View
              style={styles.loadingContainer ||= {
                alignItems: "center",
                justifyContent: "center",
                minHeight: 240
              }}
              testID="billsNewScreen/loadingState"
            >
              <ActivityIndicator size="large" />
              <Text
                style={styles.loadingText ||= {
                  color: "#6b7280",
                  marginTop: 12
                }}
                testID="billsNewScreen/loadingText"
              >
                {_("Loading...")}
              </Text>
            </View>
          }
          {!loading &&
            <Form formObjectRef={this.formObjectRef}>
              <View
                style={styles.form ||= {
                  marginTop: 24
                }}
                testID="billsNewScreen/form"
              >
                {status &&
                  <View
                    style={styles.statusMessage ||= {
                      backgroundColor: "#fee2e2",
                      borderColor: "#fecaca",
                      borderRadius: 6,
                      borderWidth: 1,
                      marginBottom: 16,
                      padding: 12
                    }}
                    testID="billsNewScreen/statusMessage"
                  >
                    <Text
                      style={styles.statusMessageText ||= {
                        color: "#991b1b",
                        fontWeight: 600
                      }}
                      testID="billsNewScreen/statusMessage/text"
                    >
                      {status.text}
                    </Text>
                  </View>
                }
                <View
                  style={styles.fieldGrid ||= {
                    columnGap: 16,
                    flexDirection: "row",
                    flexWrap: "wrap",
                    rowGap: 16
                  }}
                  testID="billsNewScreen/fieldGrid"
                >
                  <View
                    style={styles.fieldColumn ||= {
                      flexBasis: 280,
                      flexGrow: 1
                    }}
                    testID="billsNewScreen/mainColumn"
                  >
                    <OrganizationsSelect
                      blankOptionText={_("Select creditor")}
                      creditor
                      defaultValue=""
                      label={_("Creditor")}
                      name="creditorId"
                      testID="billsNewScreen/creditorInput"
                    />
                    <View
                      style={styles.fieldSpacing ||= {
                        height: 16
                      }}
                      testID="billsNewScreen/newCreditorSpacing"
                    />
                    <FormTextInput
                      defaultValue=""
                      label={_("New creditor")}
                      name="newCreditorName"
                      testID="billsNewScreen/newCreditorInput"
                    />
                    <View
                      style={styles.fieldSpacing}
                      testID="billsNewScreen/amountSpacing"
                    />
                    <FormTextInput
                      defaultValue=""
                      keyboardType="numeric"
                      label={_("Amount")}
                      name="amount"
                      testID="billsNewScreen/amountInput"
                    />
                    <View
                      style={styles.fieldSpacing}
                      testID="billsNewScreen/fileSpacing"
                    />
                    {this.fileUploadField()}
                    {uploadedFileName.length > 0 &&
                      <Text
                        style={styles.uploadedFileName ||= {
                          color: "#374151",
                          marginTop: 8
                        }}
                        testID="billsNewScreen/uploadedFileName"
                      >
                        {uploadedFileName}
                      </Text>
                    }
                  </View>
                  <View
                    style={styles.fieldColumn}
                    testID="billsNewScreen/organizationColumn"
                  >
                    <OrganizationsSelect
                      defaultValue={this.s.selectedOrganizationId}
                      label={_("Organization")}
                      name="organizationId"
                      onChangeValue={this.tt.onOrganizationChangeValue}
                      testID="billsNewScreen/organizationInput"
                    />
                    <View
                      style={styles.fieldSpacing}
                      testID="billsNewScreen/dateSpacing"
                    />
                    <FormTextInput
                      defaultValue={formatDate(new Date())}
                      label={_("Date")}
                      name="date"
                      testID="billsNewScreen/dateInput"
                    />
                  </View>
                  <View
                    style={styles.fieldColumn}
                    testID="billsNewScreen/invoiceGroupsColumn"
                  >
                    <InvoiceGroupsSelect
                      defaultValues={this.s.selectedInvoiceGroupIds}
                      label={_("Invoice groups")}
                      multiple
                      name="invoiceGroupIds"
                      onChangeValue={this.tt.onInvoiceGroupsChangeValue}
                      testID="billsNewScreen/invoiceGroupsInput"
                    />
                  </View>
                </View>
                <View
                  style={styles.buttonRow ||= {
                    flexDirection: "row",
                    justifyContent: "flex-end",
                    marginTop: 24
                  }}
                  testID="billsNewScreen/buttonRow"
                >
                  <Button
                    disabled={saving}
                    iconName="floppy-disk"
                    label={saving ? _("Saving...") : _("Save")}
                    onPress={this.tt.onSavePress}
                    testID="billsNewScreen/saveButton"
                  />
                </View>
              </View>
            </Form>
          }
        </View>
      </View>
    )
  }

  /**
   * Renders the upload field.
   * @returns {ReactNode} Upload controls.
   */
  fileUploadField() {
    return (
      <View testID="billsNewScreen/fileUploadField">
        <Text
          style={styles.fieldLabel ||= {
            color: "#374151",
            fontSize: 14,
            fontWeight: 600,
            marginBottom: 6
          }}
          testID="billsNewScreen/fileUploadField/label"
        >
          {_("Uploaded file")}
        </Text>
        {Platform.OS === "web" &&
          <Pressable
            onPress={this.tt.onChooseFilePress}
            style={styles.fileButton ||= {
              alignItems: "center",
              alignSelf: "flex-start",
              backgroundColor: "#f3f4f6",
              borderColor: "#d1d5db",
              borderRadius: 6,
              borderWidth: 1,
              flexDirection: "row",
              paddingHorizontal: 12,
              paddingVertical: 10
            }}
            testID="billsNewScreen/fileButton"
          >
            <View
              style={styles.fileButtonIcon ||= {
                marginRight: 8,
                width: 14
              }}
              testID="billsNewScreen/fileButton/icon"
            >
              <FontAwesome6 color="#111827" name="paperclip" size={14} />
            </View>
            <Text
              style={styles.fileButtonText ||= {
                color: "#111827",
                fontWeight: 600
              }}
              testID="billsNewScreen/fileButton/text"
            >
              {_("Choose file")}
            </Text>
          </Pressable>
        }
        {Platform.OS !== "web" &&
          <Text
            style={styles.inlineHint}
            testID="billsNewScreen/fileUploadField/webOnlyHint"
          >
            {_("File uploads are only supported on the web")}
          </Text>
        }
      </View>
    )
  }

  /**
   * Loads select options for the bill form.
   * @param {number} requestId Load request identifier.
   * @returns {Promise<void>} Resolves when loading finishes.
   */
  loadData = async (requestId) => {
    try {
      const [defaultInvoiceGroup, defaultOrganization, currentProfileResponse] =
        await Promise.all([
          InvoiceGroup
            .where({name: "Regninger"})
            .select(["id", "name"])
            .limit(1)
            .first(),
          Organization
            .query()
            .select(["id", "name"])
            .order([["name", "asc"], ["id", "asc"]])
            .limit(1)
            .first(),
          User.currentProfile()
      ])
      if (requestId !== this.loadDataRequestId) return
      const currentProfileUser = userFromProfileCommandResponse(currentProfileResponse)
      const primaryOrganizationId = currentProfileUser?.primary_organization_id
      const primaryOrganization = primaryOrganizationId
        ? await Organization
          .where({id: primaryOrganizationId})
          .select(["id", "name"])
          .limit(1)
          .first()
        : null

      if (requestId !== this.loadDataRequestId) return
      const selectedOrganization = primaryOrganization || defaultOrganization
      const selectedOrganizationId = selectedOrganization
        ? String(selectedOrganization.id())
        : ""
      this.s.hasOrganizations = Boolean(selectedOrganizationId)
      this.s.loading = false
      this.s.selectedInvoiceGroupIds = defaultInvoiceGroup
        ? [String(defaultInvoiceGroup.id())]
        : []
      this.s.selectedOrganizationId = selectedOrganizationId
      this.s.status =
        selectedOrganizationId.length === 0
          ? {
              kind: "error",
              text: _("No organizations found")
            }
          : null
    } catch (error) {
      if (requestId !== this.loadDataRequestId) return
      this.s.loading = false
      this.s.status = {
        kind: "error",
        text: errorMessage(error)
      }
    }
  }

  /**
   * Stores selected invoice groups.
   * @param {string | string[] | null | undefined} value Selected value from the invoice-group input.
   * @returns {void} Nothing.
   */
  onInvoiceGroupsChangeValue = (value) => {
    if (Array.isArray(value)) {
      this.s.selectedInvoiceGroupIds = value
    } else if (typeof value === "string" && value) {
      this.s.selectedInvoiceGroupIds = [value]
    } else {
      this.s.selectedInvoiceGroupIds = []
    }
  }

  /**
   * Stores the selected organization id.
   * @param {string | string[] | null | undefined} value Selected organization value.
   * @returns {void} Nothing.
   */
  onOrganizationChangeValue = (value) => {
    if (typeof value === "string") {
      this.s.selectedOrganizationId = value
    }
  }

  /**
   * Opens the browser file picker.
   * @returns {void} Nothing.
   */
  onChooseFilePress = () => {
    if (Platform.OS !== "web" || typeof document === "undefined") {
      this.s.status = {
        kind: "error",
        text: _("File uploads are only supported on the web")
      }
      return
    }
    const input = document.createElement("input")
    input.type = "file"
    input.onchange = () => {
      const file = input.files?.[0]
      if (!file) {
        this.s.uploadedFile = null
        this.s.uploadedFileName = ""
        return
      }
      const reader = new FileReader()
      reader.onload = () => {
        const dataUrl = typeof reader.result === "string" ? reader.result : ""
        const contentBase64 = dataUrl.includes(",")
          ? dataUrl.split(",", 2)[1]
          : ""
        this.s.uploadedFile = {
          contentBase64,
          contentType: file.type || "application/octet-stream",
          filename: file.name
        }
        this.s.uploadedFileName = file.name
      }
      reader.onerror = () => {
        this.s.status = {
          kind: "error",
          text: errorMessage(
            reader.error || new Error("Could not read the selected file")
          )
        }
      }
      reader.readAsDataURL(file)
    }
    input.click()
  }

  /**
   * Saves the bill form.
   * @returns {Promise<void>} Resolves when save handling finishes.
   */
  onSavePress = async () => {
    const {hasOrganizations, saving, uploadedFile} = this.s
    const {params, router} = this.tt
    const formObject = this.formObjectRef.current
    if (saving) return
    if (!formObject) {
      this.s.status = {
        kind: "error",
        text: _("Could not load the form")
      }
      return
    }
    if (!hasOrganizations) {
      this.s.status = {
        kind: "error",
        text: _("No organizations found")
      }
      return
    }
    const values = formObject.asObject()
    this.s.saving = true
    this.s.status = null
    try {
      const response = await Invoice.createBill({
        amount: typeof values.amount === "string" ? values.amount : "",
        creditorId:
          typeof values.creditorId === "string" ? values.creditorId : "",
        date: typeof values.date === "string" ? values.date : "",
        invoiceGroupIds: Array.isArray(values.invoiceGroupIds)
          ? values.invoiceGroupIds
          : typeof values.invoiceGroupIds === "string" && values.invoiceGroupIds
            ? [values.invoiceGroupIds]
            : [],
        newCreditorName:
          typeof values.newCreditorName === "string"
            ? values.newCreditorName
            : "",
        organizationId:
          typeof values.organizationId === "string" && values.organizationId
            ? values.organizationId
            : this.s.selectedOrganizationId,
        uploadedFile
      })
      const redirectPath =
        typeof response.redirectPath === "string" && response.redirectPath
          ? response.redirectPath
          : typeof response.invoiceId === "string" && response.invoiceId
            ? `/invoices/${response.invoiceId}`
            : ""
      if (!redirectPath) {
        throw new Error(_("Could not save the bill"))
      }
      router.push(buildRoutePath(params, redirectPath))
    } catch (error) {
      this.s.saving = false
      this.s.status = {
        kind: "error",
        text: errorMessage(error)
      }
    }
  }
}

/** @type {NamedExoticComponent<BillsNewScreenProps>} */
const BillsNewScreenComponent = memo(shapeComponent(BillsNewScreen))

export default BillsNewScreenComponent
